<script setup lang="ts">
import ButtonClose from '@/components/button/ButtonClose.vue'
import { messageLogin, showMessageLogin } from '@/event/account/alert/messageLogin'
import { toggleAlertLogin } from '@/event/account/alert/showAlertLogin'
import ButtonLinkRegisterHappy from '../../register/button/ButtonLinkRegisterHappy.vue'
import { toggleAlertLoginHappy } from '@/happyTheme/utils/account/login/toggleAlertLoginHappy'
import { useI18n } from 'vue-i18n'

const { t, te } = useI18n()

// Translate a backend key if it exists in the i18n catalog,
// otherwise return the raw string (fallback for legacy messages).
function tr(key: string): string {
    if (!key) return ''
    return te(key) ? t(key) : key
}
</script>

<template>
    <section class="section-message-alert-login-happy">
        <div class="div-overlay-login-alert-happy" @click="toggleAlertLoginHappy"></div>

        <Transition
            name="transition-message-login"
            enter-active-class="transition-all duration-300 ease-in-out"
            leave-active-class="transition-all duration-300 ease-in-out "
            enter-from-class="opacity-0 translate-x-full"
            enter-to-class="opacity-100 translate-x-0"
            leave-from-class="opacity-100 translate-x-0"
            leave-to-class="opacity-0 translate-x-full"
            :key="'MessageLogin'"
        >
            <div
                v-if="
                    messageLogin.errors &&
                    messageLogin.errors.length > 0 &&
                    showMessageLogin
                "
                :class="messageLogin.class ? messageLogin.class : 'hidden'"
                class=""
            >
                <div :class="`div-header-alert-login-happy-${messageLogin.class}`">
                    <ButtonClose
                        :function="toggleAlertLogin"
                        :hover-color="
                            messageLogin.class === 'success'
                                ? 'success-happy'
                                : 'error-happy'
                        "
                    />
                </div>
                <div class="div-content-alert-login-happy">
                    <ul
                        v-for="(error, index) in messageLogin.errors"
                        :key="index"
                        class="bullet-happy"
                    >
                        <li class="font-semibold text-base">{{ tr(error) }}</li>
                    </ul>
                    <div v-if="messageLogin.action == 'not-exist'" class="pt-3">
                        <!-- <ButtonLinkRegister /> -->
                    </div>
                </div>
            </div>
            <div
                v-else-if="
                    messageLogin.message &&
                    showMessageLogin &&
                    (!messageLogin.errors || messageLogin.errors.length === 0)
                "
                :class="messageLogin.class ? messageLogin.class : 'hidden'"
                class=" "
            >
                <div :class="`div-header-alert-login-happy-${messageLogin.class}`">
                    <ButtonClose
                        :function="toggleAlertLogin"
                        :hover-color="
                            messageLogin.class === 'success'
                                ? 'success-happy'
                                : 'error-happy'
                        "
                    />
                </div>
                <div class="div-content-alert-login-happy">
                    <p class="text-center font-semibold">
                        {{ tr(messageLogin.message) }}
                    </p>
                    <div
                        v-if="messageLogin.action == 'not-exist'"
                        class="pt-4 w-full flex justify-center"
                    >
                        <!-- <ButtonLinkRegister v-if="isThemeDefault" /> -->
                        <ButtonLinkRegisterHappy />
                    </div>
                </div>
            </div>
        </Transition>
    </section>
</template>

<style scoped>
.section-message-alert-login-happy {
    @apply fixed w-full h-full z-20 flex justify-center font-medium;
}

.div-overlay-login-alert-happy {
    @apply w-full h-full;
}

.error {
    /* refactored: per-theme error body -> semantic danger */
    @apply absolute z-10 rounded-md pb-2 flex flex-col items-center justify-center gap-2 min-w-[19rem] max-w-[19rem] max-h-fit;
    @apply bottom-10 right-2;
    @apply bg-t-danger text-t-on-accent;
    /* refactored: ifo-dark için zarif dark-pill toast —
       solid red kutu yerine dark card + danger border + danger text,
       subtle red-tinted glow (Ekle butonunun tarzıyla uyumlu). */
    @apply happy-ifo-dark:bg-t-card
           happy-ifo-dark:text-t-danger
           happy-ifo-dark:border
           happy-ifo-dark:border-t-danger/40
           happy-ifo-dark:shadow-[0_8px_24px_-8px_rgba(248,81,73,0.35)]
           happy-ifo-dark:rounded-xl;
}

.div-header-alert-login-happy-error {
    /* refactored: per-theme error header -> semantic danger */
    @apply flex flex-col items-end bg-t-danger w-full rounded-t-md px-3 py-2;
    /* refactored: ifo-dark'ta header ayrı şeritte değil — şeffaf, X kart
       içinde sağ üst köşede sade dursun. */
    @apply happy-ifo-dark:bg-transparent happy-ifo-dark:rounded-t-xl;
}

.success {
    /* refactored: per-theme success body -> semantic accent */
    @apply absolute z-10 shadow-sm rounded-md pb-2 flex flex-col items-center justify-center gap-2 min-w-[19rem] max-w-[19rem] max-h-fit;
    @apply bottom-10 right-2;
    @apply bg-t-accent text-t-on-accent;
    @apply happy-purple-gradient:bg-pink-dark;
    /* refactored: ifo-dark için zarif dark-pill toast — solid amber kutu
       (screenshot'ta "içler acısı" yorumu) yerine dark card + amber border
       + amber text + amber-glow shadow. Ekle butonunun dark pill tarzıyla
       tutarlı. */
    @apply happy-ifo-dark:bg-t-card
           happy-ifo-dark:text-t-accent
           happy-ifo-dark:border
           happy-ifo-dark:border-t-accent/40
           happy-ifo-dark:shadow-[0_8px_24px_-8px_rgba(212,160,23,0.35)]
           happy-ifo-dark:rounded-xl;
}

.div-header-alert-login-happy-success {
    /* refactored: per-theme success header -> semantic accent-hover */
    @apply flex flex-col items-end bg-t-accent-hover w-full rounded-t-md px-3 py-2;
    @apply happy-purple-gradient:bg-pink-dark;
    /* refactored: ifo-dark'ta header ayrı bant değil — transparent, X sade. */
    @apply happy-ifo-dark:bg-transparent happy-ifo-dark:rounded-t-xl;
}

.div-content-alert-login-happy {
    @apply px-3 py-2;
}

.bullet-happy {
    @apply relative pl-3 list-disc;
}

@media screen and (min-width: 500px) {
    .success,
    .error {
        @apply right-8;
    }
}
</style>
